import { useEffect, type RefObject } from "react";

const isElementFullyInViewport = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;

  return rect.top >= 0 && rect.bottom <= viewportHeight;
};

type CreatedCommentTarget = {
  commentId: number;
};

interface UseCreatedCommentScrollOptions {
  createdCommentTarget: CreatedCommentTarget | null;
  clearCreatedCommentTarget: () => void;
  hasMoreComments: boolean;
  isLoadingMoreComments: boolean;
  handleLoadMoreComments: () => Promise<void>;
}

export const useCreatedCommentScroll = ({
  createdCommentTarget,
  clearCreatedCommentTarget,
  hasMoreComments,
  isLoadingMoreComments,
  handleLoadMoreComments,
}: UseCreatedCommentScrollOptions): void => {
  useEffect(() => {
    if (!createdCommentTarget) return;

    const { commentId } = createdCommentTarget;
    let attempts = 0;
    const maxAttempts = 40;
    const retryMs = 120;
    let isLoadingMoreRequested = false;

    const tryScroll = () => {
      attempts += 1;
      const target = document.querySelector<HTMLElement>(
        `[data-comment-id="${commentId}"]`,
      );
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        clearCreatedCommentTarget();
        return;
      }

      if (
        hasMoreComments &&
        !isLoadingMoreComments &&
        !isLoadingMoreRequested
      ) {
        isLoadingMoreRequested = true;
        void handleLoadMoreComments().finally(() => {
          isLoadingMoreRequested = false;
        });
      }

      if (attempts >= maxAttempts) {
        clearCreatedCommentTarget();
      }
    };

    const intervalId = window.setInterval(tryScroll, retryMs);
    tryScroll();

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    clearCreatedCommentTarget,
    createdCommentTarget,
    handleLoadMoreComments,
    hasMoreComments,
    isLoadingMoreComments,
  ]);
};

interface UseRevealLoadMoreAfterCollapseOptions {
  shouldRevealLoadMoreAfterCollapse: boolean;
  clearRevealLoadMoreAfterCollapse: () => void;
  isExpanded: boolean;
  hasMoreComments: boolean;
  hasCreatedCommentTarget: boolean;
  loadMoreControlsRef: RefObject<HTMLDivElement | null>;
}

export const useRevealLoadMoreAfterCollapse = ({
  shouldRevealLoadMoreAfterCollapse,
  clearRevealLoadMoreAfterCollapse,
  isExpanded,
  hasMoreComments,
  hasCreatedCommentTarget,
  loadMoreControlsRef,
}: UseRevealLoadMoreAfterCollapseOptions): void => {
  useEffect(() => {
    if (!shouldRevealLoadMoreAfterCollapse) return;
    if (isExpanded) return;
    if (!hasMoreComments || hasCreatedCommentTarget) {
      clearRevealLoadMoreAfterCollapse();
      return;
    }

    const loadMoreControlsElement = loadMoreControlsRef.current;
    if (!loadMoreControlsElement) {
      clearRevealLoadMoreAfterCollapse();
      return;
    }

    if (!isElementFullyInViewport(loadMoreControlsElement)) {
      loadMoreControlsElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }

    clearRevealLoadMoreAfterCollapse();
  }, [
    clearRevealLoadMoreAfterCollapse,
    hasCreatedCommentTarget,
    hasMoreComments,
    isExpanded,
    loadMoreControlsRef,
    shouldRevealLoadMoreAfterCollapse,
  ]);
};

interface UseReplyToggleRevealOptions {
  replyToggleScrollTargetId: number | null;
  clearReplyToggleScrollTargetId: () => void;
}

export const useReplyToggleReveal = ({
  replyToggleScrollTargetId,
  clearReplyToggleScrollTargetId,
}: UseReplyToggleRevealOptions): void => {
  useEffect(() => {
    if (replyToggleScrollTargetId === null) return;

    let attempts = 0;
    const maxAttempts = 10;
    let frameId = 0;

    const scrollToToggle = () => {
      attempts += 1;
      const toggleContainer = document.querySelector<HTMLElement>(
        `[data-reply-toggle-comment-id="${replyToggleScrollTargetId}"]`,
      );

      if (toggleContainer) {
        if (!isElementFullyInViewport(toggleContainer)) {
          toggleContainer.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
        clearReplyToggleScrollTargetId();
        return;
      }

      if (attempts < maxAttempts) {
        frameId = window.requestAnimationFrame(scrollToToggle);
        return;
      }

      clearReplyToggleScrollTargetId();
    };

    frameId = window.requestAnimationFrame(scrollToToggle);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [clearReplyToggleScrollTargetId, replyToggleScrollTargetId]);
};
